import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Search, Trash2, X } from "lucide-react";
import { ConvertToProject } from "./ConvertToProject";
import { WorkflowPanel } from "./AdminReview";

const STATUSES = [
  "received", "under_review", "info_required", "verification", "assessment",
  "community_assessment", "approved", "waiting_funding", "planning",
  "in_progress", "completed", "not_approved",
];
const CATS = ["individual", "family", "empowerment", "community", "organisation", "education", "food", "emergency", "agriculture", "health", "water", "energy", "women_youth", "partnership"];

export default function AdminApplications() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const load = useCallback(async () => {
    const params = [];
    if (status) params.push(`status=${status}`);
    if (category) params.push(`category=${category}`);
    const r = await api.get(`/admin/applications${params.length ? "?" + params.join("&") : ""}`);
    setItems(r.data);
  }, [status, category]);
  useEffect(() => { document.title = "Admin · Applications"; load(); }, [load]);

  const view = async (a) => {
    const { data } = await api.get(`/admin/applications/${a.id}`);
    setSelected(data);
    setNewStatus(data.status);
    setAdminNotes(data.admin_notes || "");
    setNote("");
  };

  const save = async () => {
    if (!selected) return;
    try {
      const patch = {};
      if (newStatus !== selected.status) { patch.status = newStatus; patch.note = note; }
      if (adminNotes !== (selected.admin_notes || "")) patch.admin_notes = adminNotes;
      if (Object.keys(patch).length === 0) { toast.info("Nothing to save"); return; }
      const { data } = await api.put(`/admin/applications/${selected.id}`, patch);
      setSelected(data);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete application?")) return;
    await api.delete(`/admin/applications/${id}`); toast.success("Deleted"); setSelected(null); load();
  };

  const filtered = items.filter((a) => {
    if (!q) return true;
    const s = ((a.full_name || "") + " " + (a.organisation_name || "") + " " + (a.community_name || "") + " " + a.serial + " " + a.email + " " + a.phone).toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div data-testid="admin-applications">
      <h1 className="font-serif text-3xl font-bold mb-2">Applications</h1>
      <p className="text-[#0a0d12]/60 text-sm mb-6">Every request received. Track through the workflow.</p>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="relative sm:col-span-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0d12]/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serial, name, phone, email…" className="w-full pl-9 pr-3 py-2.5 border border-black/15 rounded-md text-sm" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2.5 border border-black/15 rounded-md text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-2.5 border border-black/15 rounded-md text-sm">
          <option value="">All categories</option>
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="text-sm text-[#0a0d12]/60 self-center">{filtered.length} results</div>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f8f4e8] text-left">
            <tr>
              <th className="p-3">Serial</th><th className="p-3">Type</th><th className="p-3">Applicant</th>
              <th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3">Submitted</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} className="border-t border-black/5 hover:bg-[#f8f4e8]/40 cursor-pointer" onClick={() => view(a)} data-testid={`app-row-${a.serial}`}>
                <td className="p-3 font-mono text-xs">{a.serial}</td>
                <td className="p-3 capitalize text-xs">{a.applicant_type}</td>
                <td className="p-3">{a.full_name || a.organisation_name || a.community_name || "—"}<div className="text-[10px] text-[#0a0d12]/50">{a.email}</div></td>
                <td className="p-3 text-xs capitalize">{a.primary_category?.replace(/_/g, " ")}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded text-xs bg-[#155e3f] text-[#d6aa43] capitalize">{(a.status || "").replace(/_/g, " ")}</span></td>
                <td className="p-3 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right"><button onClick={(e) => { e.stopPropagation(); remove(a.id); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#0a0d12]/50">No applications.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" data-testid="app-detail-drawer">
            <div className="p-6 border-b border-black/5 flex items-start justify-between gap-4">
              <div>
                <div className="font-mono text-sm text-[#0d1724]">{selected.serial}</div>
                <h2 className="font-serif text-2xl text-[#0a0d12] font-bold">{selected.full_name || selected.organisation_name || selected.community_name}</h2>
                <div className="text-xs text-[#0a0d12]/60 mt-1 capitalize">{selected.applicant_type} · {selected.primary_category?.replace(/_/g, " ")}</div>
              </div>
              <button onClick={() => setSelected(null)}><X size={22} /></button>
            </div>
            <div className="p-6 space-y-5">
              <Section title="Contact">
                <KV k="Email" v={selected.email} />
                <KV k="Phone" v={selected.phone} />
                <KV k="Address" v={selected.address || selected.office_address} />
                <KV k="Location" v={`${selected.state || ""}${selected.lga ? " / " + selected.lga : ""}`} />
              </Section>
              <Section title="Problem">
                <p className="text-sm text-[#0a0d12]/85 whitespace-pre-wrap">{selected.problem_description}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <KV k="Duration" v={selected.problem_duration?.replace(/_/g, " ")} />
                  <KV k="Seriousness" v={selected.seriousness} />
                </div>
              </Section>
              <Section title="Support requested">
                <p className="text-sm text-[#0a0d12]/85 whitespace-pre-wrap">{selected.support_description}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <KV k="Estimated value" v={selected.estimated_value ? `₦${Number(selected.estimated_value).toLocaleString()}` : "—"} />
                  <KV k="Assistance type" v={selected.assistance_type} />
                </div>
              </Section>
              {selected.expected_impact && (
                <Section title="Expected impact"><p className="text-sm text-[#0a0d12]/85 whitespace-pre-wrap">{selected.expected_impact}</p></Section>
              )}
              {selected.help_needs?.length > 0 && (
                <Section title="Help needs">
                  <div className="flex flex-wrap gap-1">
                    {selected.help_needs.map((h) => <span key={h} className="px-2 py-0.5 rounded bg-[#f8f4e8] text-xs">{h.replace(/_/g, " ")}</span>)}
                  </div>
                </Section>
              )}
              {selected.linked_beneficiary_serial && (
                <Section title="Linked beneficiary"><div className="font-mono text-sm">{selected.linked_beneficiary_serial}</div></Section>
              )}
              <Section title="Status history">
                <ol className="space-y-2">
                  {(selected.status_history || []).map((h, i) => (
                    <li key={`${h.at}-${h.status}-${h.note || ''}`} className="text-xs border-l-2 border-[#d6aa43] pl-3">
                      <div className="font-semibold capitalize">{(h.status || "").replace(/_/g, " ")}</div>
                      <div className="text-[#0a0d12]/60">{h.note || ""}</div>
                      <div className="text-[10px] text-[#0a0d12]/50">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
                    </li>
                  ))}
                </ol>
              </Section>

              <WorkflowPanel collection="applications" recordId={selected.id} onChanged={load} />

              <ConvertToProject key={selected.id + selected.linked_project_serial} application={selected} onConverted={() => { view(selected); load(); }} />

              <div className="p-4 rounded-md border border-[#d6aa43]/30 bg-[#f8f4e8]">
                <h3 className="font-serif text-lg font-bold mb-3">Update status</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="px-3 py-2 border border-black/15 rounded-md text-sm bg-white" data-testid="app-status-select">
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note for history" className="px-3 py-2 border border-black/15 rounded-md text-sm" data-testid="app-status-note" />
                </div>
                <div className="mt-3">
                  <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Internal admin notes</label>
                  <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={3} className="w-full px-3 py-2 border border-black/15 rounded-md text-sm" data-testid="app-admin-notes" />
                </div>
                <button onClick={save} className="dk-btn-primary mt-3 !py-2 text-sm" data-testid="app-save">Save changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2">{title}</h3>
    <div>{children}</div>
  </div>
);
const KV = ({ k, v }) => (<div className="text-sm"><span className="text-[#0a0d12]/60">{k}: </span><span className="font-semibold">{v || "—"}</span></div>);
